import type {
  NoteSummaryDTO,
  NoteSummaryFeedbackInput,
  NoteSummaryFeedbackResult,
} from '@bower/shared';
import type { HttpClient } from './http-client.types';

export function createArtifact(http: HttpClient, artifactData: unknown) {
  return http.post('/artifacts', artifactData);
}

export function getArtifact(http: HttpClient, artifactId: string, workspaceId?: string) {
  const url = `/artifacts/${artifactId}`;
  return workspaceId
    ? http.get(url, { headers: { 'x-workspace-id': workspaceId } })
    : http.get(url);
}

export function updateArtifact(
  http: HttpClient,
  artifactId: string,
  artifactData: unknown,
  opts?: { sessionEnd?: boolean; allowEmpty?: boolean },
) {
  const headers: Record<string, string> = {};
  if (opts?.sessionEnd) headers['x-bower-session-end'] = '1';
  // Signals a user-intended clear so the backend's empty-doc guard steps aside
  // for this write only (focused delete-all in the editor). See useNoteDetail.
  if (opts?.allowEmpty) headers['x-bower-allow-empty-content'] = '1';
  const config = Object.keys(headers).length > 0 ? { headers } : undefined;
  return http.put(`/artifacts/${artifactId}`, artifactData, config);
}

export function patchArtifact(http: HttpClient, artifactId: string, field: string, value: unknown) {
  return http.patch(`/artifacts/${artifactId}`, { field, value });
}

export function deleteArtifact(http: HttpClient, artifactId: string) {
  return http.delete(`/artifacts/${artifactId}`);
}

export function bulkDeleteArtifacts(http: HttpClient, artifactIds: string[]) {
  // retries: 0 — non-idempotent destructive POST; see bulkDeleteProjects.
  return http.request({ method: 'POST', url: '/artifacts/bulk-delete', data: { artifactIds } }, 0);
}

export function mergeArtifacts(http: HttpClient, artifactIds: string[]) {
  // retries: 0 — merge soft-deletes up to 19 source notes; a replayed merge
  // after a landed-but-timed-out first attempt re-runs against already-
  // deleted sources (PR #512 review; same class as the bulk-delete POSTs).
  return http.request({ method: 'POST', url: '/artifacts/merge', data: { artifactIds } }, 0);
}

export function getAllArtifacts(http: HttpClient, params: Record<string, string | number> = {}) {
  if (!params || Object.keys(params).length === 0) return http.get('/artifacts');
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');
  return http.get(qs ? `/artifacts?${qs}` : '/artifacts');
}

export function getProjectArtifacts(
  http: HttpClient,
  projectId: string,
  params: Record<string, string | number> = {},
) {
  const base = `/collections/${projectId}/artifacts`;
  if (!params || Object.keys(params).length === 0) return http.get(base);
  const qs = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== '')
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');
  return http.get(qs ? `${base}?${qs}` : base);
}

export function generateNoteTitle(http: HttpClient, artifactId: string) {
  return http.post(`/artifacts/${artifactId}/generate-title`, {});
}

// === Note AI Summary ===

export function getNoteSummary(http: HttpClient, artifactId: string) {
  return http.get<NoteSummaryDTO>(`/artifacts/${artifactId}/summary`);
}

export function generateNoteSummary(http: HttpClient, artifactId: string) {
  // retries: 0 — each attempt is a 2-5s LLM call; the http client's
  // default 5xx retry would stack with any caller-level retry into
  // multiple LLM spends per click. Failures surface to the panel's
  // explicit Retry affordance instead.
  return http.request<NoteSummaryDTO>(
    { method: 'post', url: `/artifacts/${artifactId}/summary/generate`, data: {} },
    0,
  );
}

export function submitNoteSummaryFeedback(
  http: HttpClient,
  artifactId: string,
  body: NoteSummaryFeedbackInput,
) {
  // retries: 0 — the score upserts on a deterministic id server-side, so a
  // replayed POST is harmless, but there's no reason to auto-retry a
  // fire-and-forget observability write on 5xx.
  return http.request<NoteSummaryFeedbackResult>(
    { method: 'post', url: `/artifacts/${artifactId}/summary/feedback`, data: body },
    0,
  );
}
